"use client"

import { useMemo, useState } from "react"
import { Loader2, Phone, XCircle } from "lucide-react"
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "../ui/dialog"
import { Button } from "../ui/button"
import { Card } from "../ui/card"
import { Checkbox } from "../ui/checkbox"
import { Badge } from "../ui/badge"
import { formatPhone } from "../../lib/format-utils"

interface CancelSuspensionLine {
  id: string
  ddd?: string | null
  numero?: string | null
  planName?: string | null
  plano?: string | null
  requerSuspensao?: boolean
  excluirDoContrato?: boolean
  estaSuspensa?: boolean
}

interface CancelSuspensionModalProps {
  open: boolean
  lines: CancelSuspensionLine[]
  contractLabel?: string
  loading?: boolean
  onClose: () => void
  onConfirm: (lineIds: string[]) => Promise<void> | void
}

export function CancelSuspensionModal({
  open,
  lines,
  contractLabel,
  loading = false,
  onClose,
  onConfirm,
}: CancelSuspensionModalProps) {
  const pendingLines = useMemo(
    () => lines.filter((line) => (line.requerSuspensao || line.excluirDoContrato) && !line.estaSuspensa),
    [lines]
  )
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set())
  const [processing, setProcessing] = useState(false)

  const selectedPendingIds = Array.from(selectedIds).filter((id) => pendingLines.some((line) => line.id === id))

  const toggleLine = (lineId: string, checked: boolean) => {
    setSelectedIds((prev) => {
      const next = new Set(prev)
      if (checked) next.add(lineId)
      else next.delete(lineId)
      return next
    })
  }

  const handleConfirm = async () => {
    if (selectedPendingIds.length === 0) return
    setProcessing(true)
    try {
      await onConfirm(selectedPendingIds)
      setSelectedIds(new Set())
    } finally {
      setProcessing(false)
    }
  }

  return (
    <Dialog open={open} onOpenChange={(nextOpen) => !nextOpen && onClose()}>
      <DialogContent className="sm:max-w-2xl max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <XCircle className="h-5 w-5 text-orange-600" />
            Cancelar suspensão pendente
          </DialogTitle>
          <DialogDescription>
            {contractLabel ? `Contrato ${contractLabel}. ` : ""}
            Selecione somente linhas em que o cliente desistiu da suspensão.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3">
          <div className="flex flex-wrap items-center gap-2">
            <Button
              size="sm"
              variant="outline"
              onClick={() => setSelectedIds(new Set(pendingLines.map((line) => line.id)))}
              disabled={loading || processing || pendingLines.length === 0}
            >
              Selecionar todas pendentes
            </Button>
            <Button
              size="sm"
              onClick={handleConfirm}
              disabled={loading || processing || selectedPendingIds.length === 0}
            >
              {processing ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : null}
              Cancelar suspensão selecionadas
            </Button>
            <span className="text-xs text-muted-foreground">{selectedPendingIds.length} selecionada(s)</span>
          </div>

          {loading ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
            </div>
          ) : pendingLines.length === 0 ? (
            <Card className="p-4 text-sm text-muted-foreground">
              Nenhuma linha pendente de suspensão neste contrato.
            </Card>
          ) : (
            <div className="space-y-2">
              {pendingLines.map((line) => (
                <Card key={line.id} className="p-3">
                  <div className="flex items-center gap-3">
                    <Checkbox
                      checked={selectedIds.has(line.id)}
                      onCheckedChange={(checked) => toggleLine(line.id, checked === true)}
                      aria-label={`Selecionar linha ${line.ddd || ""}${line.numero || ""}`}
                    />
                    <Phone className="h-4 w-4 text-muted-foreground" />
                    <div className="flex-1">
                      <p className="font-medium">{formatPhone(line.ddd || "", line.numero || null)}</p>
                      <p className="text-sm text-muted-foreground">{line.planName || line.plano || "Plano não informado"}</p>
                    </div>
                    <Badge className="bg-orange-100 text-orange-700 border-orange-300">Pendente</Badge>
                  </div>
                </Card>
              ))}
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  )
}
